import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Eye, Edit, Trash2, RefreshCw, Lock, Download, FileText, FileDown, Send, ClipboardCheck, Table, ChevronDown, Search, X, AlertTriangle } from 'lucide-react';
import { AnaliseVerificacoesModal } from '@/components/apreciacao/AnaliseVerificacoesModal';
import { toast } from 'sonner';
import { apiService } from '@/services/api';
import { useWebSocket, LockEvent } from '@/hooks/useWebSocket';
import { useProjeto } from '@/contexts/projeto-hook';
import { useAuth } from '@/contexts/auth-hook';
import { formatarDataPtBr } from '@/utils/date-format';
import { useI18n } from '@/contexts/i18n-hook';

/**
 * Criticidade agregada de uma apreciação, na escala do cliente (Arcelor Mittal).
 * Antes isto casava com o rótulo 'Crítico' e a faixa HRN 20–25, que vinham da
 * escala antiga do backend — nenhum dos dois é mais gravado.
 */
function classificarCriticidade(riscos: any[]): 'critica' | 'alta' | 'baixa' | 'na' {
  if (!riscos || riscos.length === 0) return 'na';

  const rotulo = (r: any) => String(r.classificacaoRisco ?? '').toLowerCase();
  const hrn = (r: any) => Number(r.hrnAtual) || 0;

  const critico = riscos.some(
    (r) => ['inaceitável', 'inaceitavel', 'extremo', 'crítico', 'critico'].includes(rotulo(r)) || hrn(r) >= 500
  );
  if (critico) return 'critica';

  const alto = riscos.some(
    (r) => ['muito alto', 'alto'].includes(rotulo(r)) || hrn(r) >= 50
  );
  return alto ? 'alta' : 'baixa';
}

/**
 * Tom de cada status. O tom é suave de propósito: com 158 de 178 apreciações
 * tramitadas, sete cores saturadas na mesma tabela viravam ruído — o contraste
 * passou a servir para separar o que exige trabalho do que já terminou, não
 * para pintar cada linha.
 *
 * O rótulo mora no dicionário (`apreciacoes.status.badge.<valor>`), indexado
 * pelo próprio valor gravado no banco.
 */
const STATUS_TOM: Record<string, string> = {
  rascunho: 'bg-amber-50 text-amber-800 dark:bg-amber-900/25 dark:text-amber-200',
  em_revisao: 'bg-blue-50 text-schmersal-blue dark:bg-blue-900/25 dark:text-blue-200',
  aprovado_internamente: 'bg-teal-50 text-teal-800 dark:bg-teal-900/25 dark:text-teal-200',
  aprovado: 'bg-green-50 text-green-800 dark:bg-green-900/25 dark:text-green-200',
  a_tramitar: 'bg-indigo-50 text-indigo-800 dark:bg-indigo-900/25 dark:text-indigo-200',
  tramitado: 'bg-purple-50 text-purple-800 dark:bg-purple-900/25 dark:text-purple-200',
  tramitado_apos_revisao: 'bg-violet-50 text-violet-800 dark:bg-violet-900/25 dark:text-violet-200',
  arquivado: 'bg-slate-100 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300',
};

/** Ordem em que os status aparecem nos contadores — do mais cru ao arquivado. */
const STATUS_ORDEM = ['rascunho', 'em_revisao', 'aprovado_internamente', 'aprovado', 'a_tramitar', 'tramitado', 'tramitado_apos_revisao', 'arquivado'];

/** Status que ainda dependem de alguém. Alimenta o "aguardando ação" do topo. */
const STATUS_EM_ABERTO = ['rascunho', 'em_revisao'];

const CRITICIDADE_META = {
  critica: { tom: 'bg-red-50 text-red-700 dark:bg-red-900/25 dark:text-red-200', ponto: 'bg-red-600' },
  alta: { tom: 'bg-orange-50 text-orange-700 dark:bg-orange-900/25 dark:text-orange-200', ponto: 'bg-orange-500' },
  baixa: { tom: 'bg-green-50 text-green-700 dark:bg-green-900/25 dark:text-green-200', ponto: 'bg-green-600' },
  na: { tom: 'bg-slate-100 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300', ponto: 'bg-slate-400' },
} as const;

function normalizarStatus(status?: string | null): string {
  const chave = String(status ?? 'rascunho').toLowerCase();
  return chave in STATUS_TOM ? chave : 'rascunho';
}

/**
 * Data de emissão e datas de guia são dia cheio gravado em UTC; formatar no
 * fuso local jogaria 25/02 para 24/02 no Brasil.
 */
const DATA_UTC: Intl.DateTimeFormatOptions = { timeZone: 'UTC' };

const BOTAO_SECUNDARIO =
  'inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg border border-rg-line dark:border-schmersal-gray-dark ' +
  'text-[13px] font-semibold text-rg-ink-2 dark:text-white bg-white dark:bg-schmersal-gray-darker ' +
  'hover:bg-rg-head dark:hover:bg-schmersal-blue focus:outline-none focus:ring-2 focus:ring-schmersal-blue/40 ' +
  'disabled:opacity-50 transition-colors duration-150';

export function ApreciacoesList() {
  const { t, formatarData, formatarDataHora } = useI18n();
  const { projeto } = useProjeto();
  const { isAdmin } = useAuth();
  const [apreciacoes, setApreciacoes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  /**
   * Falha de carga é diferente de lista vazia. Zerar a tabela num erro de rede
   * fazia o usuário ler "nenhuma apreciação encontrada" e concluir que tinha
   * perdido os documentos — aqui o que já estava na tela permanece.
   */
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  const [exportando, setExportando] = useState(false);
  const [gerandoTabela, setGerandoTabela] = useState(false);
  const [downloading, setDownloading] = useState<Set<string>>(new Set());

  const addDownloading = (id: string, type: 'pdf' | 'word') =>
    setDownloading((prev) => new Set(prev).add(`${id}-${type}`));
  const removeDownloading = (id: string, type: 'pdf' | 'word') =>
    setDownloading((prev) => { const s = new Set(prev); s.delete(`${id}-${type}`); return s; });
  const isDownloading = (id: string, type: 'pdf' | 'word') => downloading.has(`${id}-${type}`);
  const [filtros, setFiltros] = useState({
    status: '',
    busca: '',
    grd: '',
  });
  const [selecionadas, setSelecionadas] = useState<string[]>([]);
  // Exportar/gerar tabela saíram da barra principal: são ações de lote, usadas
  // de vez em quando, e cinco botões contornados lado a lado não deixavam
  // nenhum primário à vista.
  const [menuAcoesAberto, setMenuAcoesAberto] = useState(false);
  const menuAcoesRef = useRef<HTMLDivElement>(null);
  const [modalAnaliseAberto, setModalAnaliseAberto] = useState(false);
  const modalGuiaInicial = {
    aberto: false,
    modo: 'nova' as 'nova' | 'existente',
    guiaId: '',
    destinatario: '',
    observacoes: '',
    erro: '',
    gerando: false,
  };
  const [modalGuia, setModalGuia] = useState(modalGuiaInicial);
  const [guiasExistentes, setGuiasExistentes] = useState<Array<{
    id: string;
    numero: string;
    destinatario: string;
    criadoEm: string;
    totalItens: number;
  }>>([]);
  const [carregandoGuias, setCarregandoGuias] = useState(false);
  const [modalRemover, setModalRemover] = useState<{ aberto: boolean; id: string; numero: string; titulo: string; senha: string; erro: string }>({
    aberto: false,
    id: '',
    numero: '',
    titulo: '',
    senha: '',
    erro: '',
  });
  const { connected, onLockEvent } = useWebSocket();

  useEffect(() => {
    if (projeto) {
      carregarApreciacoes();
    }
  }, [projeto, filtros]);

  // Escutar eventos de lock em tempo real
  useEffect(() => {
    if (!connected) return;

    const unsubscribe = onLockEvent((event: LockEvent) => {
      if (event.tipoRecurso === 'apreciacao') {
        // Atualizar a apreciação específica na lista
        setApreciacoes((prev) =>
          prev.map((apreciacao) => {
            if (apreciacao.id === event.recursoId) {
              if (event.action === 'locked' || event.action === 'updated') {
                return {
                  ...apreciacao,
                  lock: {
                    bloqueado: true,
                    usuarioId: event.usuarioId,
                    usuarioNome: event.usuarioNome,
                    expiresAt: event.expiresAt
                  }
                };
              } else if (event.action === 'unlocked') {
                return {
                  ...apreciacao,
                  lock: { bloqueado: false }
                };
              }
            }
            return apreciacao;
          })
        );
      }
    });

    return unsubscribe;
  }, [connected, onLockEvent]);

  const carregarApreciacoes = async () => {
    if (!projeto) return;
    
    setLoading(true);
    try {
      const response = await apiService.listarApreciacoes({
        projetoId: projeto.id,
        limit: 1000,
        status: filtros.status || undefined,
        busca: filtros.busca || undefined,
        guiaId: filtros.grd || undefined,
      });

      if (response.success && response.data) {
        setApreciacoes(response.data);
        setErroCarga(null);
      } else {
        console.error('Erro ao carregar apreciações:', response.message);
        setErroCarga(response.message || t('apreciacoes.lista.erro.carregar'));
      }
    } catch (error) {
      console.error('Erro ao carregar apreciações:', error);
      setErroCarga(
        navigator.onLine
          ? t('apreciacoes.lista.erro.semServidor')
          : t('apreciacoes.lista.erro.offline')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRemover = (id: string, numero: string, titulo: string) => {
    setModalRemover({ aberto: true, id, numero, titulo, senha: '', erro: '' });
  };

  const handleConfirmarRemover = async () => {
    if (modalRemover.senha !== modalRemover.numero) {
      setModalRemover((prev) => ({ ...prev, erro: t('apreciacoes.lista.modalRemover.codigoIncorreto') }));
      return;
    }

    try {
      const response = await apiService.removerApreciacao(modalRemover.id);
      if (response.success) {
        setModalRemover({ aberto: false, id: '', numero: '', titulo: '', senha: '', erro: '' });
        carregarApreciacoes();
      } else {
        setModalRemover((prev) => ({
          ...prev,
          erro: t('apreciacoes.lista.modalRemover.erroDetalhe', {
            detalhe: response.message || t('apreciacoes.erroDesconhecido'),
          }),
        }));
      }
    } catch (error) {
      console.error('Erro ao remover apreciação:', error);
      setModalRemover((prev) => ({ ...prev, erro: t('apreciacoes.lista.modalRemover.erro') }));
    }
  };

  const handleExportarExcel = async () => {
    setExportando(true);
    try {
      const response = await apiService.exportarTodasApreciacoes();
      if (!response.success || !response.data) {
        toast.error(response.message || t('apreciacoes.lista.excel.erroExportar'));
        return;
      }

      const lista = response.data as any[];
      const XLSX = await import('xlsx');
      const { saveAs } = await import('file-saver');

      // Aba 1: Apreciações (uma linha por apreciação)
      const linhasApreciacoes = lista.map((a) => ({
        'Número': a.numero || '',
        'Título': a.titulo || '',
        'Status': a.status || '',
        'Revisão': a.revisao ?? '',
        'Projeto': a.projeto?.nome || '',
        'Modelo': a.maquina?.modelo || '',
        'Fabricante': a.maquina?.fabricante || '',
        'Série': a.maquina?.serie || '',
        'Ativo - Item': a.ativo?.item || '',
        'Emitente': a.emitente || '',
        'Aprovador': a.aprovador?.nome || '',
        'Qtd. Riscos': a.riscos?.length ?? 0,
      }));

      // Aba 2: Riscos (uma linha por risco, com referência à apreciação)
      const linhasRiscos = lista.flatMap((a) =>
        (a.riscos || []).map((r: any) => ({
          'Apreciação Nº': a.numero || '',
          'Apreciação Título': a.titulo || '',
          'Projeto': a.projeto?.nome || '',
          'Risco Nº': r.numero ?? '',
          'Parte': r.parte || '',
          'Tipo Perigo': r.tipoPerigo || '',
          'Categoria': r.categoria || '',
          'Ciclo de Vida': r.cicloVida || '',
          'Evento Perigoso': r.eventoPerigoso || '',
          'Consequências': r.consequencias || '',
          'Gravidade Atual': r.gravidadeAtual ?? '',
          'Frequência Atual': r.frequenciaAtual ?? '',
          'Exposição Atual': r.exposicaoAtual ?? '',
          'Probabilidade Atual': r.probabilidadeAtual ?? '',
          'HRN Atual': r.hrnAtual ?? '',
          'HRN Futuro': r.hrnFuturo ?? '',
          'Classificação Risco': r.classificacaoRisco || '',
          'Conforme Atual': r.conformeAtual ? 'Sim' : 'Não',
          'Medidas de Controle': r.medidasControle || '',
          'Adequações Técnicas': r.adequacoesTecnicas || '',
          'PLR': r.plr || '',
          'SIL': r.sil || '',
          'Categoria Segurança': r.categoriaSeguranca || '',
        }))
      );

      const wb = XLSX.utils.book_new();
      const wsApreciacoes = XLSX.utils.json_to_sheet(linhasApreciacoes);
      const wsRiscos = XLSX.utils.json_to_sheet(linhasRiscos);
      XLSX.utils.book_append_sheet(wb, wsApreciacoes, 'Apreciações');
      XLSX.utils.book_append_sheet(wb, wsRiscos, 'Riscos');

      const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const dataAtual = new Date().toISOString().slice(0, 10);
      saveAs(blob, `apreciacoes-${dataAtual}.xlsx`);
      toast.success(t('apreciacoes.lista.excel.sucesso', { total: lista.length }));
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      toast.error(t('apreciacoes.lista.excel.erroArquivo'));
    } finally {
      setExportando(false);
    }
  };

  // Gera um Excel de controle com Área, Tag, N° APR (número da apreciação) e Emissão.
  // Usa as apreciações já carregadas (o list traz o ativo completo). Se houver
  // selecionadas, exporta só elas; caso contrário, exporta todas as filtradas.
  // Cabeçalhos e data ficam em pt-BR de propósito: a planilha é entregue ao
  // cliente nesse formato, e mudar de idioma junto com a tela quebraria o
  // controle dele.
  const handleGerarTabelaControle = async () => {
    const base = selecionadas.length > 0
      ? apreciacoes.filter((a) => selecionadas.includes(a.id))
      : apreciacoes;

    if (base.length === 0) {
      toast.warning(t('apreciacoes.lista.tabelaControle.nenhuma'));
      return;
    }

    setGerandoTabela(true);
    try {
      const linhas = base.map((a) => ({
        'ÁREA': a.ativo?.area || '',
        'TAG DO EQUIPAMENTO': a.ativo?.tag || '',
        'N° APR NO PRAXIS': a.numero || '',
        'EMISSÃO': a.dataEmissao ? formatarDataPtBr(a.dataEmissao) : '',
      }));

      const XLSX = await import('xlsx');
      const { saveAs } = await import('file-saver');
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(linhas);
      ws['!cols'] = [{ wch: 32 }, { wch: 32 }, { wch: 18 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(wb, ws, 'Controle APR');

      const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const dataAtual = new Date().toISOString().slice(0, 10);
      saveAs(blob, `controle-apr-${dataAtual}.xlsx`);
      toast.success(t('apreciacoes.lista.tabelaControle.sucesso', { total: linhas.length }));
    } catch (error) {
      console.error('Erro ao gerar tabela de controle:', error);
      toast.error(t('apreciacoes.lista.tabelaControle.erro'));
    } finally {
      setGerandoTabela(false);
    }
  };

  // Busca as imagens em alta resolução das partes/riscos e faz merge (igual ao editor).
  // Sem isso, o PDF/Word saem sem as fotos das partes (buscarApreciacao não traz as imagens).
  const carregarRiscosComImagens = async (apreciacaoId: string, riscos: any[]) => {
    try {
      const { data: hi } = await apiService.buscarRiscosImagens(apreciacaoId, 'high');
      if (!hi) return riscos;
      return riscos.map((r: any) => {
        const hq = hi[r.id];
        if (!hq) return r;
        return {
          ...r,
          fotoAtualUrl1: hq.fotoAtualUrl1 || r.fotoAtualUrl1,
          fotoAtualUrl2: hq.fotoAtualUrl2 || r.fotoAtualUrl2,
          fotoAtualUrl3: hq.fotoAtualUrl3 || r.fotoAtualUrl3,
          fotoAtualUrl4: hq.fotoAtualUrl4 || r.fotoAtualUrl4,
          fotoPlrUrl: hq.fotoPlrUrl || r.fotoPlrUrl,
        };
      });
    } catch (e) {
      console.error('Erro ao carregar imagens dos riscos:', e);
      return riscos;
    }
  };

  const handleDownloadWord = async (apreciacao: any) => {
    if (isDownloading(apreciacao.id, 'word')) return;
    addDownloading(apreciacao.id, 'word');
    try {
      // Processo completo (igual ao editor): buscar dados → gerar LaTeX → PDF final → converter para Word.
      const res = await apiService.buscarApreciacao(apreciacao.id);
      if (!res.success || !res.data) throw new Error('Erro ao buscar dados da apreciação');
      const { gerarLatex } = await import('@/components/apreciacao/latex/generators/latex-generator');
      const dados = res.data;
      const riscos = await carregarRiscosComImagens(apreciacao.id, dados.riscos || []);
      const latex = gerarLatex(dados, riscos);
      const pdfFinal = await apiService.exportarPdf(apreciacao.id, latex, { mode: 'final' });
      // Identificação do equipamento para a capa reconstruída pelo conversor.
      const { blob: wordBlob, filename } = await apiService.exportarWordViaILovePdf(apreciacao.id, pdfFinal, {
        nome: dados.titulo || dados.nomeMaquina || dados.maquina?.nome || dados.maquina?.descricao,
        area: dados.maquina?.area,
        tag: dados.ativo?.tag,
      });
      // O conversor NR12 nomeia pela capa que reconstruiu (ex.:
      // B0712GAP0351_0001_00.docx); esse nome vence o título da apreciação.
      const nomeBase = dados.titulo || dados.numero || apreciacao.id;
      const nomeArquivo =
        filename || `${String(nomeBase).replace(/[/\\?%*:|"<>]/g, '-').trim()}.docx`;
      const url = window.URL.createObjectURL(wordBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = nomeArquivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t('apreciacoes.lista.word.sucesso'));
    } catch (error) {
      console.error('Erro ao exportar Word:', error);
      toast.error(t('apreciacoes.lista.word.erro'));
    } finally {
      removeDownloading(apreciacao.id, 'word');
    }
  };

  const handleDownloadPdf = async (apreciacao: any) => {
    if (isDownloading(apreciacao.id, 'pdf')) return;
    addDownloading(apreciacao.id, 'pdf');
    try {
      const res = await apiService.buscarApreciacao(apreciacao.id);
      if (!res.success || !res.data) throw new Error('Erro ao buscar dados da apreciação');
      const { gerarLatex } = await import('@/components/apreciacao/latex/generators/latex-generator');
      const dados = res.data;
      const riscos = await carregarRiscosComImagens(apreciacao.id, dados.riscos || []);
      const latex = gerarLatex(dados, riscos);
      const blob = await apiService.exportarPdf(apreciacao.id, latex, { mode: 'final' });
      const nomeBase = dados.titulo || dados.numero || apreciacao.id;
      const nomeArquivo = nomeBase.replace(/[/\\?%*:|"<>]/g, '-').trim();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${nomeArquivo}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('apreciacoes.lista.pdf.sucesso'));
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.error(t('apreciacoes.lista.pdf.erro'));
    } finally {
      removeDownloading(apreciacao.id, 'pdf');
    }
  };

  const criticidadeDe = (riscos: any[]) => {
    const chave = classificarCriticidade(riscos);
    return { ...CRITICIDADE_META[chave], rotulo: t(`apreciacoes.lista.criticidade.${chave}`) };
  };

  useEffect(() => {
    if (!menuAcoesAberto) return;
    const aoClicarFora = (evento: MouseEvent) => {
      if (menuAcoesRef.current && !menuAcoesRef.current.contains(evento.target as Node)) {
        setMenuAcoesAberto(false);
      }
    };
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, [menuAcoesAberto]);

  const toggleSelecionada = (id: string) => {
    setSelecionadas((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelecionarTodas = () => {
    if (selecionadas.length === apreciacoes.length && apreciacoes.length > 0) {
      setSelecionadas([]);
    } else {
      setSelecionadas(apreciacoes.map((a) => a.id));
    }
  };

  // Mantém só IDs ainda presentes depois de filtros/recargas.
  useEffect(() => {
    setSelecionadas((prev) => prev.filter((id) => apreciacoes.some((a) => a.id === id)));
  }, [apreciacoes]);

  const carregarGuias = async () => {
    if (!projeto) return;
    setCarregandoGuias(true);
    try {
      const response = await apiService.listarGuiasRemessa(projeto.id);
      setGuiasExistentes(response.success && response.data ? response.data : []);
    } catch {
      setGuiasExistentes([]);
    } finally {
      setCarregandoGuias(false);
    }
  };

  // Carrega as GRDs do projeto (usadas no filtro e no modal de guia).
  useEffect(() => {
    if (projeto) {
      carregarGuias();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projeto]);

  const abrirModalGuia = () => {
    if (selecionadas.length === 0) {
      toast.warning(t('apreciacoes.lista.guia.selecionePeloMenosUma'));
      return;
    }
    setModalGuia({ ...modalGuiaInicial, aberto: true });
    carregarGuias();
  };

  const handleRemoverDaGuia = async (
    guia: { id: string; numero: string },
    apreciacao: { id: string; numero: string },
  ) => {
    if (!confirm(t('apreciacoes.lista.guia.confirmarRemover', { numero: apreciacao.numero, guia: guia.numero })))
      return;
    try {
      const res = await apiService.removerApreciacaoGuiaRemessa(guia.id, apreciacao.id);
      if (res.success) {
        toast.success(
          t('apreciacoes.lista.guia.removida', { numero: apreciacao.numero, guia: guia.numero })
        );
        carregarApreciacoes();
        carregarGuias();
      } else {
        toast.error(res.message || t('apreciacoes.lista.guia.erroRemover'));
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t('apreciacoes.lista.guia.erroRemover')
      );
    }
  };

  const handleGerarGuia = async () => {
    if (!projeto) return;
    const usarExistente = modalGuia.modo === 'existente';
    if (usarExistente && !modalGuia.guiaId) {
      setModalGuia((prev) => ({ ...prev, erro: t('apreciacoes.lista.guia.erroSelecioneGrd') }));
      return;
    }
    const destinatario = modalGuia.destinatario.trim();
    if (!usarExistente && !destinatario) {
      setModalGuia((prev) => ({ ...prev, erro: t('apreciacoes.lista.guia.erroDestinatario') }));
      return;
    }
    setModalGuia((prev) => ({ ...prev, erro: '', gerando: true }));
    try {
      const { blob, numero } = usarExistente
        ? await apiService.adicionarApreciacoesGuiaRemessa({
            guiaId: modalGuia.guiaId,
            apreciacaoIds: selecionadas,
          })
        : await apiService.gerarGuiaRemessa({
            projetoId: projeto.id,
            destinatario,
            observacoes: modalGuia.observacoes.trim() || null,
            apreciacaoIds: selecionadas,
          });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Guia_Remessa_${numero ?? 'sem-numero'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(
        usarExistente
          ? t('apreciacoes.lista.guia.adicionadas', { numero: numero ?? '' })
          : t('apreciacoes.lista.guia.gerada', { numero: numero ?? '' }),
      );
      setModalGuia(modalGuiaInicial);
      setSelecionadas([]);
      carregarApreciacoes();
      carregarGuias();
    } catch (error) {
      setModalGuia((prev) => ({
        ...prev,
        erro: error instanceof Error ? error.message : t('apreciacoes.lista.guia.erroGerar'),
        gerando: false,
      }));
    }
  };

  const totalNaTela = apreciacoes.length;
  const aguardandoAcao = apreciacoes.filter((a) => STATUS_EM_ABERTO.includes(normalizarStatus(a.status))).length;
  const contagemPorStatus = STATUS_ORDEM
    .map((chave) => ({
      chave,
      tom: STATUS_TOM[chave],
      total: apreciacoes.filter((a) => normalizarStatus(a.status) === chave).length,
    }))
    .filter((c) => c.total > 0);

  const resumoContagem = t(
    totalNaTela === 1 ? 'apreciacoes.lista.resumo.contagemUma' : 'apreciacoes.lista.resumo.contagemVarias',
    { total: totalNaTela }
  );

  return (
    <div className="space-y-6">
      {/* Cabeçalho: um primário à vista; o resto das ações de lote vive no menu
          "Ações" e na barra de seleção, que só aparece com linhas marcadas. */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-rg-ink dark:text-white">{t('apreciacoes.lista.titulo')}</h1>
          <p className="mt-0.5 text-[13.5px] text-rg-ink-3 dark:text-schmersal-gray-light">
            {loading
              ? t('comum.estado.carregando')
              : aguardandoAcao > 0
                ? t('apreciacoes.lista.resumo.comAguardando', {
                    contagem: resumoContagem,
                    aguardando: aguardandoAcao,
                  })
                : resumoContagem}
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button onClick={carregarApreciacoes} disabled={loading} className={BOTAO_SECUNDARIO}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {t('comum.acao.atualizar')}
          </button>

          <div className="relative" ref={menuAcoesRef}>
            <button
              type="button"
              onClick={() => setMenuAcoesAberto((v) => !v)}
              aria-expanded={menuAcoesAberto}
              aria-haspopup="menu"
              className={BOTAO_SECUNDARIO}
            >
              {t('comum.rotulo.acoes')}
              <ChevronDown className={`h-4 w-4 opacity-60 transition-transform duration-150 ${menuAcoesAberto ? 'rotate-180' : ''}`} />
            </button>

            {menuAcoesAberto && (
              <div
                role="menu"
                className="absolute right-0 mt-1.5 w-64 z-20 rounded-lg border border-rg-line dark:border-schmersal-gray-dark
                           bg-white dark:bg-schmersal-gray-darker shadow-lg p-1.5"
              >
                <button
                  role="menuitem"
                  onClick={() => { setMenuAcoesAberto(false); handleGerarTabelaControle(); }}
                  disabled={gerandoTabela}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-left text-[13px] font-medium
                             text-rg-ink-2 dark:text-white hover:bg-rg-head dark:hover:bg-schmersal-blue
                             disabled:opacity-50 transition-colors duration-150"
                  title={selecionadas.length > 0
                    ? t('apreciacoes.lista.menu.tituloTabelaSelecionadas', { total: selecionadas.length })
                    : t('apreciacoes.lista.menu.tituloTabelaTodas')}
                >
                  <Table className={`h-4 w-4 flex-none ${gerandoTabela ? 'animate-pulse' : ''}`} />
                  {gerandoTabela
                    ? t('apreciacoes.lista.menu.gerandoTabela')
                    : selecionadas.length > 0
                      ? t('apreciacoes.lista.menu.gerarTabelaContagem', { total: selecionadas.length })
                      : t('apreciacoes.lista.menu.gerarTabela')}
                </button>

                {isAdmin && (
                  <button
                    role="menuitem"
                    onClick={() => { setMenuAcoesAberto(false); handleExportarExcel(); }}
                    disabled={exportando}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-left text-[13px] font-medium
                               text-rg-ink-2 dark:text-white hover:bg-rg-head dark:hover:bg-schmersal-blue
                               disabled:opacity-50 transition-colors duration-150"
                  >
                    <Download className={`h-4 w-4 flex-none ${exportando ? 'animate-pulse' : ''}`} />
                    {exportando
                      ? t('apreciacoes.lista.menu.exportando')
                      : t('apreciacoes.lista.menu.exportarExcel')}
                  </button>
                )}
              </div>
            )}
          </div>

          <Link
            to="/apreciacoes/nova"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-schmersal-blue
                       dark:bg-schmersal-blue-light text-white text-[13px] font-semibold shadow-sm
                       hover:bg-schmersal-blue-dark dark:hover:bg-schmersal-blue
                       focus:outline-none focus:ring-2 focus:ring-schmersal-blue/40 transition-colors duration-150"
          >
            <Plus className="h-4 w-4" />
            {t('apreciacoes.lista.novaApreciacao')}
          </Link>
        </div>
      </div>

      {/* Filtros numa faixa só. Os contadores à direita são leitura, não
          controle — por isso vêm sem contorno, ao contrário dos selects. */}
      <div className="rounded-xl border border-rg-line dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darker
                      p-3 flex flex-wrap items-center gap-2.5">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-rg-ink-4" />
          <input
            type="text"
            value={filtros.busca}
            onChange={(e) => setFiltros({ ...filtros, busca: e.target.value })}
            placeholder={t('apreciacoes.lista.filtro.buscaPlaceholder')}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-rg-line dark:border-schmersal-gray-dark
                       bg-white dark:bg-schmersal-gray-darkest text-[13px] text-rg-ink-2 dark:text-white
                       placeholder-rg-ink-4 focus:outline-none focus:ring-2 focus:ring-schmersal-blue/40
                       focus:border-schmersal-blue transition-colors duration-150"
          />
        </div>

        <select
          aria-label={t('apreciacoes.lista.filtro.ariaStatus')}
          value={filtros.status}
          onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
          className="py-2 pl-3 pr-8 rounded-lg border border-rg-line dark:border-schmersal-gray-dark
                     bg-white dark:bg-schmersal-gray-darkest text-[13px] font-medium text-rg-ink-2 dark:text-white
                     focus:outline-none focus:ring-2 focus:ring-schmersal-blue/40 transition-colors duration-150"
        >
          <option value="">{t('apreciacoes.lista.filtro.statusTodos')}</option>
          {STATUS_ORDEM.map((chave) => (
            <option key={chave} value={chave}>
              {t(`apreciacoes.status.opcao.${chave}`)}
            </option>
          ))}
        </select>

        <select
          aria-label={t('apreciacoes.lista.filtro.ariaGrd')}
          value={filtros.grd}
          onChange={(e) => setFiltros({ ...filtros, grd: e.target.value })}
          className="py-2 pl-3 pr-8 rounded-lg border border-rg-line dark:border-schmersal-gray-dark
                     bg-white dark:bg-schmersal-gray-darkest text-[13px] font-medium text-rg-ink-2 dark:text-white
                     focus:outline-none focus:ring-2 focus:ring-schmersal-blue/40 transition-colors duration-150"
        >
          <option value="">{t('apreciacoes.lista.filtro.grdTodas')}</option>
          <option value="sem-grd">{t('apreciacoes.lista.filtro.semGrd')}</option>
          {guiasExistentes.map((g) => (
            <option key={g.id} value={g.id}>
              {g.numero} — {g.destinatario}
            </option>
          ))}
        </select>

        {contagemPorStatus.length > 0 && (
          <>
            <div className="hidden xl:block h-6 w-px bg-rg-line dark:bg-schmersal-gray-dark" />
            <div className="flex flex-wrap items-center gap-2">
              {contagemPorStatus.map((c) => (
                <span
                  key={c.chave}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12px] font-medium ${c.tom}`}
                >
                  {t(`apreciacoes.status.badge.${c.chave}`)}
                  <b className="font-bold tabular-nums">{c.total}</b>
                </span>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Ações de lote só existem quando há seleção — antes ocupavam a barra
          principal permanentemente, desabilitadas. */}
      {selecionadas.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl bg-schmersal-blue px-4 py-2.5 text-white">
          <span className="text-[13px] font-semibold">
            {t(
              selecionadas.length === 1
                ? 'apreciacoes.lista.selecao.uma'
                : 'apreciacoes.lista.selecao.varias',
              { total: selecionadas.length }
            )}
          </span>
          <div className="hidden sm:block h-5 w-px bg-white/30" />
          <button
            onClick={abrirModalGuia}
            className="inline-flex items-center gap-2 text-[13px] font-medium hover:underline underline-offset-4"
          >
            <Send className="h-4 w-4" />
            {t('apreciacoes.lista.selecao.guiaRemessa')}
          </button>
          <button
            onClick={() => setModalAnaliseAberto(true)}
            className="inline-flex items-center gap-2 text-[13px] font-medium hover:underline underline-offset-4"
          >
            <ClipboardCheck className="h-4 w-4" />
            {t('apreciacoes.lista.selecao.analisarVerificacoes')}
          </button>
          <button
            onClick={handleGerarTabelaControle}
            disabled={gerandoTabela}
            className="inline-flex items-center gap-2 text-[13px] font-medium hover:underline underline-offset-4 disabled:opacity-60"
          >
            <Table className={`h-4 w-4 ${gerandoTabela ? 'animate-pulse' : ''}`} />
            {t('apreciacoes.lista.selecao.gerarTabela')}
          </button>
          <button
            onClick={() => setSelecionadas([])}
            title={t('apreciacoes.lista.selecao.limpar')}
            className="ml-auto p-1 rounded-md hover:bg-white/15 transition-colors duration-150"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Falha de carga com dados ainda em tela: avisa que o que se vê pode estar velho. */}
      {erroCarga && !loading && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span className="flex-1">{erroCarga}</span>
          <button
            type="button"
            onClick={() => carregarApreciacoes()}
            className="font-medium underline underline-offset-2 hover:no-underline"
          >
            {t('comum.acao.tentarNovamente')}
          </button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64 rounded-xl border border-rg-line dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darker">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-rg-line border-t-schmersal-blue dark:border-schmersal-gray-dark dark:border-t-white mx-auto"></div>
            <p className="mt-3 text-[13px] text-rg-ink-3 dark:text-white font-medium">{t('apreciacoes.lista.carregando')}</p>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-schmersal-gray-darker rounded-xl overflow-hidden border border-rg-line dark:border-schmersal-gray-dark">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-rg-head dark:bg-schmersal-gray-dark border-b border-rg-line dark:border-schmersal-gray-darkest">
                <tr>
                  <th className="px-5 py-2.5 text-center w-11">
                    <input
                      type="checkbox"
                      aria-label={t('apreciacoes.lista.tabela.selecionarTodas')}
                      checked={apreciacoes.length > 0 && selecionadas.length === apreciacoes.length}
                      ref={(el) => {
                        if (el) el.indeterminate = selecionadas.length > 0 && selecionadas.length < apreciacoes.length;
                      }}
                      onChange={toggleSelecionarTodas}
                      className="h-4 w-4 rounded border-rg-line text-schmersal-blue focus:ring-2 focus:ring-schmersal-blue/40 cursor-pointer"
                    />
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-rg-ink-3 dark:text-schmersal-gray-light">
                    {t('apreciacoes.lista.tabela.colApreciacao')}
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-rg-ink-3 dark:text-schmersal-gray-light">
                    {t('apreciacoes.lista.tabela.colEmitente')}
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-rg-ink-3 dark:text-schmersal-gray-light">
                    {t('comum.rotulo.status')}
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-rg-ink-3 dark:text-schmersal-gray-light">
                    {t('comum.rotulo.tipo')}
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-rg-ink-3 dark:text-schmersal-gray-light">
                    {t('apreciacoes.lista.tabela.colCriticidade')}
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-rg-ink-3 dark:text-schmersal-gray-light">
                    {t('comum.rotulo.data')}
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-rg-ink-3 dark:text-schmersal-gray-light">
                    {t('apreciacoes.lista.tabela.colGrd')}
                  </th>
                  <th className="relative px-5 py-2.5">
                    <span className="sr-only">{t('comum.rotulo.acoes')}</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-schmersal-gray-darker divide-y divide-rg-hairline dark:divide-schmersal-gray-dark">
                {apreciacoes.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-5 py-14 text-center">
                      <p className="text-[13.5px] text-rg-ink-3 dark:text-white font-medium">
                        {erroCarga
                          ? t('apreciacoes.lista.vazio.erro')
                          : t('apreciacoes.lista.vazio.semDados')}
                      </p>
                      {erroCarga && (
                        <button
                          type="button"
                          onClick={() => carregarApreciacoes()}
                          className="mt-3 inline-flex items-center px-3 py-1.5 rounded-md border border-schmersal-blue text-[12.5px] font-medium text-schmersal-blue hover:bg-schmersal-blue/5"
                        >
                          {t('comum.acao.tentarNovamente')}
                        </button>
                      )}
                    </td>
                  </tr>
                ) : (
                  apreciacoes.map((apreciacao) => (
                    <tr
                      key={apreciacao.id}
                      className={`border-t border-rg-hairline dark:border-schmersal-gray-dark transition-colors duration-150 ${selecionadas.includes(apreciacao.id) ? 'bg-schmersal-blue/[0.04] dark:bg-schmersal-blue/20' : 'hover:bg-rg-head dark:hover:bg-schmersal-gray-dark'}`}
                    >
                      <td className="px-5 py-3.5 text-center w-11">
                        <input
                          type="checkbox"
                          aria-label={t('apreciacoes.lista.tabela.selecionarApreciacao', { numero: apreciacao.numero })}
                          checked={selecionadas.includes(apreciacao.id)}
                          onChange={() => toggleSelecionada(apreciacao.id)}
                          className="h-4 w-4 rounded border-rg-line text-schmersal-blue focus:ring-2 focus:ring-schmersal-blue/40 cursor-pointer"
                        />
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <div className="text-[13.5px] font-semibold text-rg-ink dark:text-white">
                              {apreciacao.titulo || apreciacao.numero}
                            </div>
                            {apreciacao.numero && (
                              <div className="mt-0.5 text-[12px] text-rg-ink-4 dark:text-schmersal-gray-light tabular-nums">
                                {apreciacao.numero}
                              </div>
                            )}
                          </div>
                          {apreciacao.lock?.bloqueado && (
                            <div 
                              className="flex items-center gap-1.5 px-2 py-1 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md"
                              title={t('apreciacoes.lista.lock.titulo', {
                                usuario: apreciacao.lock.usuarioNome || t('apreciacoes.lista.lock.usuarioPadrao'),
                                expira: apreciacao.lock.expiresAt
                                  ? formatarDataHora(apreciacao.lock.expiresAt)
                                  : t('apreciacoes.lista.lock.semExpiracao'),
                              })}
                            >
                              <Lock className="h-4 w-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                              <div className="text-xs">
                                <div className="font-medium text-yellow-800 dark:text-yellow-200">
                                  {apreciacao.lock.usuarioNome || t('apreciacoes.lista.lock.editando')}
                                </div>
                                {apreciacao.lock.expiresAt && (
                                  <div className="text-yellow-600 dark:text-yellow-400">
                                    {formatarDataHora(apreciacao.lock.expiresAt, {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <div className="text-[13.5px] text-rg-ink-3 dark:text-white">
                          {apreciacao.emitente || '—'}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <select
                          value={apreciacao.status || 'rascunho'}
                          onChange={async (e) => {
                            const novoStatus = e.target.value;
                            const res = await apiService.atualizarApreciacao(apreciacao.id, { status: novoStatus });
                            if (res.success) {
                              setApreciacoes(prev => prev.map(a =>
                                a.id === apreciacao.id ? { ...a, status: novoStatus } : a
                              ));
                            }
                          }}
                          className={`inline-flex px-2.5 py-1 text-[12px] font-semibold rounded-md border-0 cursor-pointer appearance-none focus:outline-none focus:ring-2 focus:ring-schmersal-blue/40 ${STATUS_TOM[normalizarStatus(apreciacao.status)]}`}
                          style={{ paddingRight: '1.5rem', backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\'%3E%3Cpath d=\'m6 9 6 6 6-6\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.4rem center' }}
                        >
                          {STATUS_ORDEM.map((chave) => (
                            <option key={chave} value={chave}>
                              {t(`apreciacoes.status.opcao.${chave}`)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        {isAdmin ? (
                          <select
                            value={(apreciacao.revisao ?? 1) > 1 ? 'revisao' : 'novo'}
                            onChange={async (e) => {
                              const novaRevisao = e.target.value === 'revisao' ? 2 : 1;
                              const res = await apiService.atualizarApreciacao(apreciacao.id, { revisao: novaRevisao });
                              if (res.success) {
                                setApreciacoes(prev => prev.map(a =>
                                  a.id === apreciacao.id ? { ...a, revisao: novaRevisao } : a
                                ));
                              }
                            }}
                            className={`inline-flex px-2.5 py-1 text-[12px] font-semibold rounded-md border-0 cursor-pointer appearance-none
                              focus:outline-none focus:ring-2 focus:ring-schmersal-blue/40 ${
                              (apreciacao.revisao ?? 1) > 1
                                ? 'bg-blue-50 text-schmersal-blue dark:bg-blue-900/25 dark:text-blue-200'
                                : 'bg-green-50 text-green-800 dark:bg-green-900/25 dark:text-green-200'
                            }`}
                            style={{ paddingRight: '1.5rem', backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\'%3E%3Cpath d=\'m6 9 6 6 6-6\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.4rem center' }}
                          >
                            <option value="novo">{t('apreciacoes.lista.tipo.novo')}</option>
                            <option value="revisao">{t('apreciacoes.lista.tipo.revisao')}</option>
                          </select>
                        ) : (
                          <span
                            className={`inline-flex px-2.5 py-1 text-[12px] font-semibold rounded-md ${
                              (apreciacao.revisao ?? 1) > 1
                                ? 'bg-blue-50 text-schmersal-blue dark:bg-blue-900/25 dark:text-blue-200'
                                : 'bg-green-50 text-green-800 dark:bg-green-900/25 dark:text-green-200'
                            }`}
                          >
                            {(apreciacao.revisao ?? 1) > 1
                              ? t('apreciacoes.lista.tipo.revisao')
                              : t('apreciacoes.lista.tipo.novo')}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[12px] font-semibold rounded-md
                                      ${criticidadeDe(apreciacao.riscos || []).tom}`}
                        >
                          <span className={`h-1.5 w-1.5 rounded-full ${criticidadeDe(apreciacao.riscos || []).ponto}`} />
                          {criticidadeDe(apreciacao.riscos || []).rotulo}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-[13.5px] text-rg-ink-3 dark:text-white tabular-nums">
                        {apreciacao.dataEmissao
                          ? formatarData(apreciacao.dataEmissao, DATA_UTC)
                          : '-'}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        {apreciacao.guiaRemessaItens?.length > 0 ? (
                          <div className="flex flex-col gap-1">
                            {apreciacao.guiaRemessaItens.map((item: any) => (
                              <span
                                key={item.guia.id}
                                className="inline-flex items-center w-fit pl-2.5 pr-1 py-1 text-[12px] font-semibold rounded-md tabular-nums
                                           bg-rg-head text-schmersal-blue border border-rg-line
                                           dark:bg-schmersal-blue/40 dark:text-white dark:border-transparent"
                                title={t('apreciacoes.lista.grd.tituloBadge', {
                                  destinatario: item.guia.destinatario,
                                  data: formatarData(item.guia.createdAt, DATA_UTC),
                                })}
                              >
                                {item.guia.numero}
                                <button
                                  type="button"
                                  onClick={() => handleRemoverDaGuia(item.guia, apreciacao)}
                                  title={t('apreciacoes.lista.grd.removerDesta', { numero: apreciacao.numero })}
                                  className="ml-1 flex items-center justify-center h-4 w-4 rounded-full leading-none
                                             text-schmersal-blue/70 dark:text-white/70
                                             hover:bg-red-600 hover:text-white transition-colors"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[13.5px] text-rg-ink-4 dark:text-schmersal-gray-light">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-right">
                        <div className="flex justify-end items-center gap-1">
                          <Link
                            to={`/apreciacoes/${apreciacao.id}`}
                            className="p-1.5 rounded-md text-rg-ink-3 dark:text-white hover:bg-schmersal-blue hover:text-white dark:hover:bg-schmersal-blue transition-colors duration-150"
                            title={t('comum.acao.visualizar')}
                          >
                            <Eye className="h-[17px] w-[17px]" />
                          </Link>
                          <Link
                            to={`/apreciacoes/${apreciacao.id}/editar`}
                            className="p-1.5 rounded-md text-rg-ink-3 dark:text-white hover:bg-schmersal-blue hover:text-white dark:hover:bg-schmersal-blue transition-colors duration-150"
                            title={t('comum.acao.editar')}
                          >
                            <Edit className="h-[17px] w-[17px]" />
                          </Link>
                          <button
                            onClick={() => handleDownloadPdf(apreciacao)}
                            disabled={isDownloading(apreciacao.id, 'pdf')}
                            className="p-1.5 rounded-md text-rg-ink-3 dark:text-red-400 hover:bg-red-700 hover:text-white dark:hover:bg-red-700 transition-colors duration-150 disabled:opacity-50"
                            title={t('apreciacoes.lista.acao.baixarPdf')}
                          >
                            {isDownloading(apreciacao.id, 'pdf')
                              ? <RefreshCw className="h-[17px] w-[17px] animate-spin" />
                              : <FileText className="h-[17px] w-[17px]" />
                            }
                          </button>
                          <button
                            onClick={() => handleDownloadWord(apreciacao)}
                            disabled={isDownloading(apreciacao.id, 'word')}
                            className="p-1.5 rounded-md text-rg-ink-3 dark:text-blue-400 hover:bg-blue-600 hover:text-white dark:hover:bg-blue-600 transition-colors duration-150 disabled:opacity-50"
                            title={t('apreciacoes.lista.acao.baixarWord')}
                          >
                            {isDownloading(apreciacao.id, 'word')
                              ? <RefreshCw className="h-[17px] w-[17px] animate-spin" />
                              : <FileDown className="h-[17px] w-[17px]" />
                            }
                          </button>
                          <button
                            onClick={() => handleRemover(apreciacao.id, apreciacao.numero, apreciacao.titulo)}
                            className="p-1.5 rounded-md text-rg-ink-3 dark:text-red-400 hover:bg-red-600 hover:text-white dark:hover:bg-red-600 transition-colors duration-150"
                            title={t('comum.acao.remover')}
                          >
                            <Trash2 className="h-[17px] w-[17px]" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal: Gerar guia de remessa */}
      {modalGuia.aberto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
              {t('apreciacoes.lista.modalGuia.titulo')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-schmersal-gray-light mb-4">
              {t('apreciacoes.lista.modalGuia.subtitulo', { total: selecionadas.length })}
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-white mb-1">{t('apreciacoes.lista.modalGuia.grdReferente')}</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setModalGuia((prev) => ({ ...prev, modo: 'nova', erro: '' }))}
                    className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                      modalGuia.modo === 'nova'
                        ? 'bg-schmersal-blue text-white border-schmersal-blue'
                        : 'bg-white dark:bg-schmersal-gray-darkest text-gray-700 dark:text-white border-gray-300 dark:border-schmersal-gray-dark hover:border-schmersal-blue'
                    }`}
                  >
                    {t('apreciacoes.lista.modalGuia.criarNova')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setModalGuia((prev) => ({ ...prev, modo: 'existente', erro: '' }))}
                    className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                      modalGuia.modo === 'existente'
                        ? 'bg-schmersal-blue text-white border-schmersal-blue'
                        : 'bg-white dark:bg-schmersal-gray-darkest text-gray-700 dark:text-white border-gray-300 dark:border-schmersal-gray-dark hover:border-schmersal-blue'
                    }`}
                  >
                    {t('apreciacoes.lista.modalGuia.usarExistente')}
                  </button>
                </div>
              </div>
              {modalGuia.modo === 'existente' ? (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-white mb-1">{t('apreciacoes.lista.modalGuia.grdExistente')}</label>
                  <select
                    value={modalGuia.guiaId}
                    onChange={(e) => setModalGuia((prev) => ({ ...prev, guiaId: e.target.value, erro: '' }))}
                    disabled={carregandoGuias}
                    className="w-full border border-gray-300 dark:border-schmersal-gray-dark rounded-md px-3 py-2 text-sm
                               bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                               focus:outline-none focus:ring-2 focus:ring-schmersal-blue disabled:opacity-60"
                  >
                    <option value="">
                      {carregandoGuias
                        ? t('apreciacoes.lista.modalGuia.carregandoGrds')
                        : guiasExistentes.length === 0
                          ? t('apreciacoes.lista.modalGuia.semGrds')
                          : t('apreciacoes.lista.modalGuia.selecioneGrd')}
                    </option>
                    {guiasExistentes.map((g) => (
                      <option key={g.id} value={g.id}>
                        {t('apreciacoes.lista.modalGuia.opcaoGrd', {
                          numero: g.numero,
                          destinatario: g.destinatario,
                          itens: t(
                            g.totalItens === 1
                              ? 'apreciacoes.lista.modalGuia.itemUm'
                              : 'apreciacoes.lista.modalGuia.itemVarios',
                            { total: g.totalItens }
                          ),
                          data: formatarData(g.criadoEm, DATA_UTC),
                        })}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-schmersal-gray-light mt-1">
                    {t('apreciacoes.lista.modalGuia.avisoExistente')}
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-white mb-1">{t('apreciacoes.lista.modalGuia.destinatario')}</label>
                    <input
                      type="text"
                      value={modalGuia.destinatario}
                      onChange={(e) => setModalGuia((prev) => ({ ...prev, destinatario: e.target.value, erro: '' }))}
                      placeholder={t('apreciacoes.lista.modalGuia.destinatarioPlaceholder')}
                      className="w-full border border-gray-300 dark:border-schmersal-gray-dark rounded-md px-3 py-2 text-sm
                                 bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                                 focus:outline-none focus:ring-2 focus:ring-schmersal-blue"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-white mb-1">{t('comum.rotulo.observacoes')}</label>
                    <textarea
                      value={modalGuia.observacoes}
                      onChange={(e) => setModalGuia((prev) => ({ ...prev, observacoes: e.target.value }))}
                      rows={3}
                      placeholder={t('apreciacoes.lista.modalGuia.observacoesPlaceholder')}
                      className="w-full border border-gray-300 dark:border-schmersal-gray-dark rounded-md px-3 py-2 text-sm
                                 bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                                 focus:outline-none focus:ring-2 focus:ring-schmersal-blue resize-y"
                    />
                  </div>
                </>
              )}
            </div>
            {modalGuia.erro && (
              <p className="text-sm text-red-600 dark:text-red-400 mt-3">{modalGuia.erro}</p>
            )}
            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => setModalGuia(modalGuiaInicial)}
                disabled={modalGuia.gerando}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-white bg-white dark:bg-schmersal-gray-dark
                           border border-gray-300 dark:border-schmersal-gray-dark rounded-md hover:bg-gray-50 dark:hover:bg-schmersal-gray-darkest
                           disabled:opacity-50"
              >
                {t('comum.acao.cancelar')}
              </button>
              <button
                onClick={handleGerarGuia}
                disabled={modalGuia.gerando}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-schmersal-blue rounded-md
                           hover:bg-schmersal-blue-dark disabled:opacity-60"
              >
                {modalGuia.gerando ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                {modalGuia.gerando
                  ? t('apreciacoes.lista.modalGuia.gerando')
                  : modalGuia.modo === 'existente'
                    ? t('apreciacoes.lista.modalGuia.adicionarGerarPdf')
                    : t('apreciacoes.lista.modalGuia.gerarPdf')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação com senha */}
      {modalRemover.aberto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              {t('apreciacoes.lista.modalRemover.titulo')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-schmersal-gray-light mb-3">
              {t('apreciacoes.lista.modalRemover.aviso')}
            </p>
            <div className="bg-gray-50 dark:bg-schmersal-gray-darkest rounded-md px-3 py-2 mb-4 space-y-1">
              <p className="text-xs text-gray-500 dark:text-schmersal-gray-light">
                {t('apreciacoes.lista.modalRemover.rotuloNome')}{' '}
                <span className="font-semibold text-gray-800 dark:text-white">{modalRemover.titulo || '—'}</span>
              </p>
              <p className="text-xs text-gray-500 dark:text-schmersal-gray-light font-mono">
                {t('apreciacoes.lista.modalRemover.rotuloCodigo')}{' '}
                <span className="font-bold text-gray-800 dark:text-white">{modalRemover.numero}</span>
              </p>
            </div>
            <input
              type="text"
              value={modalRemover.senha}
              onChange={(e) => setModalRemover((prev) => ({ ...prev, senha: e.target.value, erro: '' }))}
              onKeyDown={(e) => e.key === 'Enter' && handleConfirmarRemover()}
              placeholder={modalRemover.numero}
              className="w-full border border-gray-300 dark:border-schmersal-gray-dark rounded-md px-3 py-2 text-sm
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-red-500 mb-2"
              autoFocus
            />
            {modalRemover.erro && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-3">{modalRemover.erro}</p>
            )}
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setModalRemover({ aberto: false, id: '', numero: '', titulo: '', senha: '', erro: '' })}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-white bg-white dark:bg-schmersal-gray-dark
                           border border-gray-300 dark:border-schmersal-gray-dark rounded-md hover:bg-gray-50 dark:hover:bg-schmersal-gray-darkest"
              >
                {t('comum.acao.cancelar')}
              </button>
              <button
                onClick={handleConfirmarRemover}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                {t('comum.acao.excluir')}
              </button>
            </div>
          </div>
        </div>
      )}

      <AnaliseVerificacoesModal
        aberto={modalAnaliseAberto}
        apreciacaoIds={selecionadas}
        onClose={() => setModalAnaliseAberto(false)}
      />
    </div>
  );
}
